import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Image, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';

interface HowAutomatedPricingWorksModalProps {
  visible?: boolean;
  onClose: () => void;
  style?: any;
}

const histogramDarkDesktop = require('@/../public/images/partners/automated-pricing/histogram-dark.png');
const histogramLightDesktop = require('@/../public/images/partners/automated-pricing/histogram-light.png');
const histogramDarkMobile = require('@/../public/images/partners/automated-pricing/histogram-dark-mobile.png');
const histogramLightMobile = require('@/../public/images/partners/automated-pricing/histogram-light-mobile.png');

export function HowAutomatedPricingWorksModal({
  visible = false,
  onClose,
  style,
}: HowAutomatedPricingWorksModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const [showModal, setShowModal] = useState(visible);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.scrollViewContainer}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            paddingTop: 16,
          }}
        >
          <View>
            <Text type="title2">How Automated Pricing Works</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <ScrollView style={{ padding: 16 }} contentContainerStyle={{ gap: 24 }}>
          <View style={{ gap: 24 }}>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Eon's automated pricing system is designed to maximize the total
              monthly earnings of your vehicle, not just the price of a single
              rental.
            </Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Our system continuously adjusts prices based on local demand,
              seasonality, day of week, booking timing, and nearby vehicle
              availability. When demand is high, your car may book well above
              your minimum rate. When demand is lower, the system may lower the
              price to stay competitive.
            </Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Your Minimum Daily Rate sets the lowest price you're willing to
              accept. The system will never price your vehicle below this
              amount.
            </Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              The minimum rate is not the price your car will usually book for.
              It acts as a floor that protects your earnings while giving the
              pricing system flexibility.
            </Text>
          </View>

          <View
            style={{
              width: '100%',
              height: 300,
            }}
          >
            <Image
              style={{
                width: '100%',
                height: '100%',
              }}
              source={
                isDesktop() && UnistylesRuntime.themeName === 'dark'
                  ? histogramDarkDesktop
                  : isDesktop() && UnistylesRuntime.themeName === 'light'
                  ? histogramLightDesktop
                  : !isDesktop() && UnistylesRuntime.themeName === 'dark'
                  ? histogramDarkMobile
                  : histogramLightMobile
              }
              contentFit="contain"
            />
          </View>

          <View>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Setting a reasonable minimum gives the system more room to adjust
              pricing across demand conditions, keeping your vehicle booked and
              maximizing total monthly revenue.
            </Text>
          </View>

          <View style={{ paddingBottom: 24 }}>
            <Alert
              type={AlertType.BRAND}
              title="Recommendation"
              body="Choose a minimum rate you're comfortable accepting on a slower day, while still giving the system flexibility to capture more bookings."
            />
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  scrollViewContainer: {
    backgroundColor: theme.colors.background_2,
    gap: 24,
    borderRadius: 16,
    maxHeight: {
      xs: 600,
      md: 700,
    },
  },
}));
